import { api, ApiError } from "@/lib/api";

export type HubAiMcpCheck =
  | { ok: true; tools: string[]; readTools: string[]; sendTool: string | null }
  | { ok: false; code: string; message: string };

export type HubAiStatus = {
  slack: {
    configured: boolean;
    connected: boolean;
    /** Slack app name recipients see under messages Hub AI sends ("Sent using …"). */
    appName: string;
    teamName?: string;
    teamId?: string;
    userName?: string;
    connectedAt?: string;
    missingScopes?: string[];
  };
  llm: { configured: boolean; provider?: string; model?: string };
  /** Only on `?check=1`: Slack MCP initialize + tools/list with the stored token. */
  mcp?: HubAiMcpCheck;
};

export type HubAiToolCall = { name: string; ok: boolean };

/** A message the assistant drafted. Nothing is sent until the user presses Send. */
export type HubAiProposedSend = {
  id: string;
  channelId: string;
  channelName?: string;
  threadTs?: string;
  text: string;
};

export type HubAiChatReply = {
  reply: string;
  toolCalls: HubAiToolCall[];
  proposedSends: HubAiProposedSend[];
  provider?: string;
  model?: string;
  elapsedMs?: number;
};

export type HubAiMessage = { role: "user" | "assistant"; content: string };

export type HubAiSendResult = { ok: boolean; channelId: string; ts?: string; permalink?: string };

/** Hub API error code (`details.code`), e.g. `llm_not_configured`, `slack_reauth`. */
export function hubAiErrorCode(err: unknown): string {
  if (!(err instanceof ApiError) || !err.details || typeof err.details !== "object") return "";
  const code = (err.details as { code?: unknown }).code;
  return typeof code === "string" ? code : "";
}

export function hubAiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return "Couldn't reach the Hub API. Check your connection and try again.";
    // 408 is the client timeout; nginx answers a slow request with an HTML 504 page.
    if (err.status === 408 || err.status === 504 || /^\s*</.test(err.message)) {
      return "Hub AI took too long to answer. Try a narrower question.";
    }
    return err.message || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

export function fetchHubAiStatus(check = false): Promise<HubAiStatus> {
  return api<HubAiStatus>(`/api/ai/status${check ? "?check=1" : ""}`, { timeoutMs: check ? 35000 : 15000 });
}

/** Slack OAuth URL for the Hub AI Slack app. Its state is single-use and expires in 10 minutes. */
export async function startHubAiSlackConnect(): Promise<string> {
  const { authorizationUrl } = await api<{ authorizationUrl: string }>("/api/ai/slack/connect", {
    method: "POST",
    body: "{}",
    timeoutMs: 15000,
  });
  return authorizationUrl;
}

export function disconnectHubAiSlack(): Promise<{ ok: boolean; revoked?: boolean }> {
  return api("/api/ai/slack/disconnect", { method: "POST", body: "{}", timeoutMs: 20000 });
}

function localTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function askHubAi(messages: HubAiMessage[]): Promise<HubAiChatReply> {
  return api<HubAiChatReply>("/api/ai/chat", {
    method: "POST",
    body: JSON.stringify({ messages, timeZone: localTimeZone() }),
    timeoutMs: 65000,
  });
}

export function sendHubAiDraft(draft: HubAiProposedSend): Promise<HubAiSendResult> {
  return api<HubAiSendResult>("/api/ai/slack/send", {
    method: "POST",
    body: JSON.stringify({
      channelId: draft.channelId,
      text: draft.text,
      threadTs: draft.threadTs,
      proposalId: draft.id,
    }),
    timeoutMs: 35000,
  });
}

export function hubAiProviderLabel(provider?: string): string {
  if (provider === "openai") return "OpenAI";
  if (provider === "anthropic") return "Anthropic";
  return provider || "AI model";
}

/** `slack_search_public_and_private` → "Search public and private". */
export function hubAiToolLabel(name: string): string {
  const words = name.replace(/^slack_/, "").split(/[_\s]+/).filter(Boolean).join(" ");
  return words ? words[0]!.toUpperCase() + words.slice(1) : name;
}
